/**
 * Serialisation helpers for annotation layers.
 *
 * Keeps React code away from annotation class internals: callers ask the layer
 * for serialised data, or push data into a layer, without ever touching the
 * ArrowAnnotation / EllipseAnnotation / TextAnnotation classes directly.
 */

import type { AnnotationLayer } from './annotation-layer';
import { ArrowAnnotation } from './objects/arrow-annotation';
import { EllipseAnnotation } from './objects/ellipse-annotation';
import { TextAnnotation } from './objects/text-annotation';
import type { AnnotationData, IAnnotationObject } from './types';
import { AnnotationType } from './types';

export function serialiseAnnotations(layer: AnnotationLayer): AnnotationData[] {
    return layer.getAllObjects().map((obj) => obj.serialize());
}

export function hydrateAnnotations(layer: AnnotationLayer, data: AnnotationData[]): void {
    layer.clear();
    for (const item of data) {
        layer.addObject(hydrateAnnotation(item));
    }
}

/**
 * Round-trip annotation data through the annotation classes so every item comes
 * back in the canonical `serialize()` shape.
 *
 * Normalises three sources of representation drift at once: `hydrateAnnotation`
 * applies the legacy ellipse `radius` shim, the constructors fill missing style
 * fields from `DEFAULT_ANNOTATION_STYLE`, and `serialize()` emits a fixed key
 * order.
 */
export function normaliseAnnotations(data: AnnotationData[]): AnnotationData[] {
    return data.map((item) => hydrateAnnotation(item).serialize());
}

/**
 * Compare two annotation sets by value, ignoring representation differences.
 *
 * A plain `JSON.stringify` comparison is NOT equivalent: annotations persisted
 * server-side live in a Postgres `jsonb` column, which normalises object key
 * order (by key length, then bytewise), so a row read back from the API never
 * stringifies the same as freshly serialised layer data even when nothing
 * changed. Normalising both sides through `serialize()` makes the comparison
 * about the values only.
 *
 * Element order is significant, and stable in practice: a layer is hydrated in
 * the persisted array order and `addObject` appends.
 */
export function annotationsEqual(a: AnnotationData[], b: AnnotationData[]): boolean {
    return JSON.stringify(normaliseAnnotations(a)) === JSON.stringify(normaliseAnnotations(b));
}

function hydrateAnnotation(data: AnnotationData): IAnnotationObject {
    switch (data.type) {
        case AnnotationType.ARROW:
            return ArrowAnnotation.hydrate(data);
        case AnnotationType.CIRCLE:
            return EllipseAnnotation.hydrate(normaliseEllipseData(data));
        case AnnotationType.TEXT:
            return TextAnnotation.hydrate(data);
    }
}

/**
 * Back-compat shim: older snapshots persisted a single `radius` field before
 * the ellipse gained independent x/y radii. Normalise to the current shape.
 */
function normaliseEllipseData(data: AnnotationData & { type: AnnotationType.CIRCLE }) {
    const legacy = data as unknown as { radius?: number };
    return {
        ...data,
        radiusX: data.radiusX ?? legacy.radius ?? 0,
        radiusY: data.radiusY ?? legacy.radius ?? 0,
    };
}
